/**
 * Pure helpers used by the Budget page.
 *
 * Kept in a separate module so the main `Budget.jsx` stays under the
 * cyclomatic-complexity cliff that the code reviewer flagged.
 */

/**
 * Given the raw groups array from /api/budget, build the two-level
 * rollup the page renders:
 *   { desList: [
 *       { cat_sec, des_budget, des_actual,
 *         subs: [{ cat_sub, sub_budget, sub_actual, rows }] },
 *       ...
 *     ],
 *     grand_budget, grand_actual }
 *
 * The outer key (`cat_sec`) is the section LABEL (e.g. "OPERATIONS"),
 * formerly stored as `cat_des` in the DB before the 2026-06-25 rename.
 */
export function buildBudgetRollup(groups) {
  const desMap = new Map();
  groups.forEach((g) => {
    let sb = 0;
    let sa = 0;
    // Roll signed values so cat_sign=1 rows (expenses) subtract from the
    // sub-, section-, and grand-totals just like the row-level display does.
    g.rows.forEach((r) => {
      sb += r.signed_budget;
      sa += r.signed_actual ?? r.actual;
    });
    const sub = { ...g, sub_budget: sb, sub_actual: sa };
    const key = g.cat_sec || "(uncategorized)";
    if (!desMap.has(key)) {
      desMap.set(key, { cat_sec: key, subs: [], des_budget: 0, des_actual: 0 });
    }
    const bucket = desMap.get(key);
    bucket.subs.push(sub);
    bucket.des_budget += sb;
    bucket.des_actual += sa;
  });
  const desList = Array.from(desMap.values());
  const grand_budget = desList.reduce((acc, d) => acc + d.des_budget, 0);
  const grand_actual = desList.reduce((acc, d) => acc + d.des_actual, 0);
  return { desList, grand_budget, grand_actual };
}

export const fmtMoney = (n) => {
  const v = Number(n) || 0;
  const s = Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return v < 0 ? `($${s})` : `$${s}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });
};
